import { Product } from '../../model/product/product.model';

/**
 * THE tab-glyph colours: one source for the ON and OFF tint of every LINE tab,
 * in every place that draws one.
 *
 * This exists because the same two colour chains lived in BOTH
 * line-menu-button (which exports the shipped PNG) and line-home (which draws
 * the phone mockup the owner reviews). They were written to mirror each other
 * and did not: Sequoia's mockup showed #73A621/#6E7F5E against the exported
 * #8CC63F/#79876F, and every Miami change had to be typed twice or the preview
 * silently kept showing the old asset (owner 2026-08-13: "preview assetleri
 * reflect etmiyor"). line-color-set's own comment already warned about exactly
 * this for the tab FILL — "keeping two copies of the value in two components is
 * what kept letting them drift apart" — so the tints now follow the fill's rule.
 *
 * Anything that draws a tab glyph reads lineTabOn/lineTabOff. Nothing else
 * decides a tab colour.
 */

/**
 * TOKYO iOS 26 ACTIVE-GLYPH LEGIBILITY (2026-08-07, owner hit it on a live
 * phone: the selected tab's icon all but disappears behind the frosted pill).
 *
 * We do NOT draw the Liquid Glass bar or the active pill — iOS/LINE do, and the
 * pill is a LIGHTER surface than the glass around it regardless of whether the
 * theme is light or dark. So the glass cannot be "made dark to match the theme";
 * the only lever we ship is the glyph, and LINE's own rule for the _g slots
 * (already quoted in this component's docs) is that it must read on BOTH black
 * and white.
 *
 * Tokyo's ON glyph failed it badly: tuned as a light violet for a dark bar, it
 * scored 10.97:1 on dark glass but only 1.59:1 on the light pill. A colour
 * clears 3:1 against BOTH only inside a mid-luminance window:
 *
 *     3:1 vs white      -> relative luminance <= 0.30
 *     3:1 vs dark glass -> relative luminance >= 0.13
 *
 * so the fix keeps the hue, keeps it saturated, and lands its luminance inside
 * that window. SCOPE (owner 2026-08-07): Tokyo's _g slots ONLY. Other themes
 * measure the same way but are deliberately left alone, and the classic slots
 * are a separate problem with a separate answer.
 */
const GLASS_LUM_MIN = 0.16;   // a little inside 0.13 so antialiased edges keep the margin
const GLASS_LUM_MAX = 0.27;   // a little inside 0.30, same reason
const GLASS_MIN_SAT = 55;     // stay a colour on the glass, never a grey tint

function hexToRgb( hex: string ): [ number, number, number ] | null {
  const m = /^#?([0-9a-f]{6})$/i.exec( ( hex || '' ).trim() );
  if ( !m ) {
    return null;
  }
  const n = parseInt( m[ 1 ], 16 );
  return [ ( n >> 16 ) & 255, ( n >> 8 ) & 255, n & 255 ];
}

function relLum( [ r, g, b ]: [ number, number, number ] ): number {
  const f = ( v: number ) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow( ( c + 0.055 ) / 1.055, 2.4 );
  };
  return 0.2126 * f( r ) + 0.7152 * f( g ) + 0.0722 * f( b );
}

function rgbToHsl( [ r, g, b ]: [ number, number, number ] ): [ number, number, number ] {
  const R = r / 255, G = g / 255, B = b / 255;
  const max = Math.max( R, G, B ), min = Math.min( R, G, B );
  const l = ( max + min ) / 2;
  let h = 0, s = 0;
  if ( max !== min ) {
    const d = max - min;
    s = l > 0.5 ? d / ( 2 - max - min ) : d / ( max + min );
    h = max === R ? ( G - B ) / d + ( G < B ? 6 : 0 )
      : max === G ? ( B - R ) / d + 2
        : ( R - G ) / d + 4;
    h *= 60;
  }
  return [ h, s * 100, l * 100 ];
}

function hslToRgb( h: number, s: number, l: number ): [ number, number, number ] {
  const S = s / 100, L = l / 100;
  const c = ( 1 - Math.abs( 2 * L - 1 ) ) * S;
  const x = c * ( 1 - Math.abs( ( ( h / 60 ) % 2 ) - 1 ) );
  const m = L - c / 2;
  const seg: [ number, number, number ][] = [
    [ c, x, 0 ], [ x, c, 0 ], [ 0, c, x ], [ 0, x, c ], [ x, 0, c ], [ c, 0, x ],
  ];
  const [ r, g, b ] = seg[ Math.min( 5, Math.floor( ( ( ( h % 360 ) + 360 ) % 360 ) / 60 ) ) ];
  return [ ( r + m ) * 255, ( g + m ) * 255, ( b + m ) * 255 ];
}

function toHex( [ r, g, b ]: [ number, number, number ] ): string {
  const to = ( v: number ) => Math.round( Math.max( 0, Math.min( 255, v ) ) )
    .toString( 16 ).padStart( 2, '0' );
  return `#${ to( r ) }${ to( g ) }${ to( b ) }`.toUpperCase();
}

/**
 * Move a colour into the "reads on black AND white" luminance window, keeping
 * its hue (the theme's identity) and keeping it saturated. Colours already in
 * the window come back untouched.
 */
export function glassSafeGlyph( hex: string ): string {
  const rgb = hexToRgb( hex );
  if ( !rgb ) {
    return hex;
  }
  const lum = relLum( rgb );
  const [ h, s ] = rgbToHsl( rgb );
  if ( lum >= GLASS_LUM_MIN && lum <= GLASS_LUM_MAX && s >= GLASS_MIN_SAT ) {
    return hex;
  }
  const sat = Math.max( s, GLASS_MIN_SAT );
  const target = Math.min( GLASS_LUM_MAX, Math.max( GLASS_LUM_MIN, lum ) );
  // HSL lightness is not luminance, so binary-search the lightness that lands
  // this hue on the target luminance.
  let lo = 0, hi = 100, out = hslToRgb( h, sat, 50 );
  for ( let i = 0; i < 24; i++ ) {
    const mid = ( lo + hi ) / 2;
    out = hslToRgb( h, sat, mid );
    if ( relLum( out ) < target ) {
      lo = mid;
    } else {
      hi = mid;
    }
  }
  return toHex( out );
}

/**
 * Miami's FADED colour circle 1 — Plum Night lightened toward the skin's own
 * pale pink rather than toward white, which is what keeps it a dusty PINK
 * instead of drifting to grey mauve (owner 2026-08-13: "soluk koyu pembe,
 * circle 1 gibi ama soluğu").
 *
 * One value doing two jobs, deliberately: the idle tab glyphs AND the unentered
 * passcode palms. Circle 1 at full strength was tried for the passcode first and
 * was far too heavy — 11.4:1 against the lock screen's #FFECF3 while the lit
 * aqua palm sits at 1.70:1, so the empty dots outweighed the filled ones. This
 * lands at 2.93:1 there: plainly visible, no longer shouting.
 *
 * It also clears the iOS-26 glass BOTH ways (3.32:1 on white, 6.32:1 on black),
 * which is the harder constraint and the reason this exact value was picked —
 * luminance 0.266 sits inside the 0.10-0.30 window where that is possible at all.
 */
export const MIAMI_FADED_CIRCLE_1 = '#B17F92';

/**
 * The ACTIVE tab glyph colour.
 *
 * Most themes settled on ONE colour for every tab. The themes that have NOT had
 * a pass still use the original per-slot plan — home/VOOM/shopping green,
 * chats/wallet/mini blue, calls/news/today pink — drawn from the site colour
 * circles. News-Today-Calls share one swappable slot in the app so that family
 * is one colour; VOOM green and Wallet blue keep neighbours distinct in the
 * usual Home-Chats-VOOM-News-Wallet order.
 *
 * `slot` selects within that plan. Callers drawing a single tab (the phone
 * mockup's active Home) pass 'home'; omitting it falls back to the same green
 * that slot would take, which is what the mockup used before this became one
 * function.
 */
export function lineTabOn( p: Product, slot?: string ): string {
  const byKey: { [ key: string ]: string } = {
    // Miami: Electric Aqua, COLOUR CIRCLE 4 — the same green the passcode's lit
    // palm canopy uses, so the tab bar and the lock screen light up as one theme
    // (owner 2026-08-13). It replaces Miami Pink, which was tried first and made
    // the idle and active states two shades of the same hue.
    //
    // Measured, because it IS the weaker choice on paper: 1.93:1 on white glass
    // and 1.38:1 on the pale tab fill, against Miami Pink's 2.83 and 2.03. What
    // it buys is worth more than those numbers. On black glass it is 10.89:1,
    // and against the idle glyph it goes from 1.17:1 to 1.72:1 — the pink pair
    // was two pinks a shade apart, this is cyan against dusty rose, which the
    // eye separates by hue long before luminance. That is the same trade
    // Roraima's tab colours already run on.
    'miami': '#00D1C1',
    // Istanbul: Rooftop Red pulled one step deeper. The theme is turquoise, so a
    // turquoise ON is the obvious pick and it is exactly the trap here — this
    // skin's CLASSIC tab strip is the saturated cyan #4AC3E0, and every Iznik /
    // Bosphorus turquoise measured against it lands near 1:1, i.e. the active
    // glyph disappears into the bar it sits on. Measured, not assumed. So the
    // theme's warm counterpart carries the active state instead and the
    // turquoise keeps the idle one.
    //
    // The MAIDEN'S TOWER's own wall, three steps deeper (owner 2026-08-14:
    // "home dışındaki seçili olan buttonlar da galata kulesinin sütlü
    // kahverengi duvarlarının rengi gibi olsun"). It ties every active tab to
    // the hero glyph: the Home tab draws the tower in stone and red, and the
    // rest of the row lights up in the same stone.
    //
    // The depth is not a style choice. The icon's literal wall cream #D5C3AB
    // measures 1.72:1 on white and 1.20:1 on the cyan strip, i.e. an active
    // glyph you cannot see; the glyph module's own already-deepened #B49A6C is
    // still only 2.70 and 1.31. Held at the same hue and taken to #A68A57 it
    // reaches 3.28:1 on white and 3.22:1 on the iOS 26 glass while still
    // reading as milky coffee rather than as brown ink — the owner asked it
    // back up a step from a first #967C4D ("biraz daha açık sütlü kahve
    // olsun"), and 3.28 is the most light it can take and still clear 3:1.
    //
    // BOTH states have to be plainly visible, which is what set the depth
    // (owner 2026-08-14: "ON OFF hepsi gorunur olmali"). The binding surface is
    // the cyan strip: clearing 3:1 on #4AC3E0 needs a relative luminance at or
    // under 0.127, so BOTH glyphs are forced dark and the ON/OFF separation
    // cannot come from lightness. It comes from HUE instead, red against teal,
    // which the eye resolves long before luminance at tab size — and the depth
    // is then set so ON still out-measures OFF on every surface rather than
    // relying on hue alone.
    //
    // 3.28:1 on white, 3.22:1 on the iOS 26 glass, 1.54:1 on the flat cyan —
    // and that last number is the one to read carefully. The glyphs do NOT sit
    // on flat #4AC3E0 on the classic bar; they sit on the menu STRIP, whose sky
    // has just been lightened for exactly this reason. Against that light band
    // it measures comfortably. The flat cyan is only what LINE paints around
    // the strip.
    'istanbul': '#A68A57',
    // Paris: DEEP EIFFEL, the tower's own gold (owner 2026-08-14: "tüm menu
    // buttons ON renkleri home ON rengindeki doygun sarı olsun"). The Home tab
    // draws the Eiffel lit at night and the rest of the row now lights up in the
    // same gold, so the bar reads as one object rather than as a gold tower
    // beside four unrelated marks.
    //
    // DEEPENED, and the reason is the strip it now sits on. Deep Eiffel itself,
    // #DE9D2E, was measured against a night-river strip where it scored 8.69:1.
    // The owner then had that strip rebuilt LIGHT — a beige Haussmann terrace
    // under a pink sky — and on that band the same gold measures a median 1.98:1
    // while the idle rose measures 2.24. That is not merely weak, it is the
    // inversion this family keeps shipping: the idle row louder than the active
    // one.
    //
    // Held at Deep Eiffel's hue and taken down TWO steps: median 2.77:1 on the
    // strip, 3.23:1 on the near-white iOS 26 capsule, 2.67:1 on the active pill,
    // and comfortably above the idle rose's 1.98 either way. A third step was
    // tried and pulled back — at #9A711D the numbers are better still and the
    // colour has stopped being gold ("çok yaptın ya, kopkoyu oldu"). This is the
    // last stop before amber turns into bronze, which matters because the whole
    // rule here is that every ON glyph is the Home tab tower's own gold.
    'paris': '#B48322',
    // Rio: RAINFOREST, the theme's own accent and the lit face of the Cristo the
    // Home tab draws — the family rule that every ON glyph takes the Home glyph's
    // colour, so the bar reads as one object.
    //
    // PROVISIONAL. Rio's skin is derived rather than sampled and its classic strip
    // is still being drawn, so the two surfaces this has to clear are both
    // provisional too. What is already decided is the DIRECTION: both surfaces are
    // LIGHT (a mint capsule, a dawn-lit bay), so as on Paris the active glyph must
    // be the DARKER mark and the idle one the lighter — the reverse of every
    // dark-skin theme here, and the inversion this family keeps shipping when the
    // strip changes lightness after the colours are picked.
    'rio-de-janeiro': '#028134',
    // Tallinn: DEEP COBALT, and the reason it beats Cobalt Spire here is measured
    // rather than felt. The classic glyph turns out to sit at y 13..93 of the
    // 150-unit strip — measured off the preview's own tab bar, not guessed — so
    // the town's ROOFS are inside the glyph box, and a red pantile roof is the
    // darkest thing this scene has. Cobalt Spire #425EB8 measures 1.87:1 on that
    // roof, which is nothing; Deep Cobalt clears 3.0.
    //
    // The usual objection to Deep Cobalt is that it fails the black iOS-26 capsule
    // at 2.2 — but this skin's capsule is sampled WHITE (#FFFFFF with an #EAEAEA
    // pill), so the black face is not a surface this theme ever lands on. It
    // measures 12.63 there and ~8 on the wall's limestone.
    'tallinn': '#184092',
    // Ocean: DEEP SEA GREEN, the theme's own mint taken well down.
    //
    // The value here was #2BA189, chosen against a tab fill of #123A40 — deep
    // water. The owner's screenshots put the classic strip's fill at the header's
    // BRIGHT TEAL #2FB8A7, and on that the old teal measures 1.48:1: the active
    // glyph all but disappeared on the one surface it exists to sit on, while the
    // idle slate beside it held 2.17 and read as the selected tab.
    //
    // One PNG serves both layouts, so the pick has to clear the near-white iOS-26
    // pill and the teal strip at once: 3.61:1 on the pill (#E0E7E7), 4.32 on the
    // glass bar, 3.41 on the strip's own water — which is the surface the glyph
    // band actually sits on — and 1.84 on the tab fill. The idle slate sits below
    // it on every one of them, which is the ordering this family keeps shipping
    // inverted.
    //
    // It sat four steps deeper than this for one round (#0E5248, 7.22 on the pill)
    // and the owner called it too dark, which it was: a near-black jellyfish is a
    // bug on a bright skin, not a creature. Lifting it costs the idle slate too —
    // the pair collapses if only one end moves — so #507078 went up with it.
    //
    // Lifted twice more after that (#0E5248 -> #177A66 -> here). The next step is
    // the last one available: at #1E8D77 the pill reads 3.26 against the idle
    // slate's 2.60 and the two stop being an ON and an OFF.
    //
    // What it costs is the glow, and the glow is not lost — the drawing keeps it.
    // The lit jellyfish still carries the pale crest inside its bell, and on a
    // bright skin a pale crest inside a deep creature is the more literal reading
    // anyway: a medusa lit from above rather than one lighting itself.
    'ocean': '#1B8570',
    // Salda: WARM SAND, from the shell family rather than the lake.
    //
    // It was a deep lake teal, chosen when this theme's whole chrome was believed
    // to be turquoise-over-sand. The owner's screenshots kept the turquoise header
    // but put it on the passcode keypad too, and asked for the tab glyphs to come
    // off the SHELLS instead — the same warm sand the passcode dots are made of,
    // so the tab bar and the lock screen read as one theme.
    //
    // The literal request was passcode dot 1 (#D6B184) and it cannot carry a tab:
    // this skin's classic strip ends on a SALMON input bar, and #D6B184 measures
    // 1.16 there — the glyph is simply not on the screen. Any idle tone paler than
    // it would then outweigh it, which is the inversion this family keeps
    // shipping. So the ON takes the DEEPEST member of the same shell family, which
    // is the lightest value that survives every surface: 2.90 on the iOS-26 pill,
    // 3.47 on the glass bar, 3.58 on the strip's own sky and 2.09 on the tab fill.
    'salda': '#A87F4E',
    // Prague: CANDLE GOLD, the palace lamps in the theme's own icon.
    //
    // Every warm colour this theme actually owns fails the iOS-26 rule outright:
    // its Warm Amber #EFBA5A measures 1.77:1 on white, its Wheat #E3CA77 1.62.
    // Those are the colours of the icon, and they are unusable as a glyph for the
    // same reason they look so good in it — they are LIGHT, and the rule is that
    // an _g glyph clears 3:1 on both faces of glass we do not draw.
    //
    // Held at the amber's hue and taken down to a relative luminance of 0.279,
    // the top edge of the window where that is possible at all: 3.19 on white,
    // 6.58 on black, 5.34 on the strip's night sky. It is still recognisably the
    // lamp colour, which the next step down (#AD7F28, 3.59 on white) is not — at
    // that depth it has become bronze.
    'prague': '#B5892F',
    // Sydney: DEEP HARBOUR, the theme's own #0F91C7 taken two steps down.
    //
    // The theme's blue at full strength measures 3.23:1 on the strip's sky and
    // 2.62 on the pale-harbour tab fill — the fill is the binding surface here,
    // because that is what LINE paints around the strip, and 2.62 is not enough.
    // Two steps down: 4.75 on white, 4.42 on black, 3.58 on the tab fill, 3.98 on
    // the strip's water. Everything clears with room.
    'sydney': '#0E7BA8',
    // Reynisfjara: DRIFTWOOD, the theme's own — and the only warm thing anywhere
    // on this theme, which is exactly why it carries the active state.
    //
    // This palette has no saturated colour in it at all, by design, so the usual
    // move (take the theme's brightest hue down into the glass window) has nothing
    // to take. What it does have is one warm family against a great deal of cold
    // grey, and hue is a better separator than weight when weight is scarce — the
    // trade Miami and Roraima both settled on.
    //
    // 4.57:1 on the strip's dusk sky, 4.10 on white and 5.24 on black: the whole
    // set clears, and it clears without the colour having to become anything the
    // theme is not.
    // ...and it is the bird's own WHITE now, not the driftwood tan it was.
    //
    // A puffin is black and white with an orange bill, and the tan one read as a
    // generic brown bird — the strip and the passcode both drew dark-bodied birds
    // while the tab drew this, so the theme showed two different animals. Black is
    // not available: on a #20262C tab fill a black bird is a hole. The other half
    // of the bird is, and it is the half that carries the identity anyway once the
    // bill and the eye-line are on top of it.
    //
    // It also fixes a weak pair. The tan measured 4.57 on the tab fill against the
    // idle slate's 4.04 — half a stop apart, separated mostly by temperature. This
    // is 7.46 against 4.04, which is an ordering rather than a nuance.
    'reynisfjara': '#AEB6C0',
    // Spring: the HUMMINGBIRD'S OWN ROSE, colour circle 3 — the family rule that
    // every active glyph takes the Home glyph's colour, so the bar reads as one
    // object rather than as a bird beside four unrelated marks.
    //
    // It is also the only way this theme's tab row separates at all. Everything
    // else here is green: the skin's header, the strip's meadow, the tab fill. A
    // green active glyph on a green strip is the Istanbul trap, where a turquoise
    // ON on a turquoise bar measured near 1:1 and simply vanished.
    //
    // 3.71:1 on the strip's sky, 4.32 on white, 4.85 on black, 3.43 on the pale
    // tab fill. Everything clears, and its luminance of 0.202 sits comfortably
    // inside the window where an _g glyph can clear both faces of the glass.
    'spring': '#BA6173',
    // Vienna: IMPERIAL GOLD, the theme's own Warm Amber taken two steps down.
    //
    // Same problem Prague has and the same answer. The gold that makes the icon
    // beautiful is LIGHT — #DCA826 measures 2.42:1 on white and its cream sibling
    // 1.62 — and the _g rule is that the glyph clears 3:1 on both faces of glass
    // we do not draw. At a relative luminance of 0.259 this is still plainly gold
    // and clears everything: 3.14 on white, 6.68 on black, 4.76 on the strip's
    // ceiling, 3.98 on the wine tab fill.
    // Vienna: SIENNA — the violin's own varnish, so the selected tab is the same
    // wood as the Home glyph and the row reads as one object. The family rule.
    //
    // 4.47:1 on the sampled tab fill against the idle pair's 2.07, so the selected
    // tab outweighs an idle one by better than two to one. The derived values had
    // this BACKWARDS on this skin: gold #B08430 measured 3.11 and the idle dusty
    // rose #9C6E74 measured 3.93, which is an idle tab shouting over a live one.
    'vienna': '#A85E2C',
    // Madrid: FLAMENCO RED, colour circle 4 — the fan's own colour, so every
    // active tab is the same red as the Home glyph and the row reads as one
    // object. The family rule.
    //
    // It is also the only colour on this theme that can carry the state. Madrid's
    // palette is warm all the way through — cream, saffron, ochre, terracotta —
    // and its own gold #DCA225 measures 2.09:1 on the cream body, which is a
    // colour you cannot see. The red is 4.90 there and 4.36 on the tab fill, with
    // a luminance of 0.147 that sits inside the window where an _g glyph clears
    // both faces of the glass.
    // Madrid: FLAMENCO RED — kept, and confirmed by the sample. The owner's own
    // screenshot shows LINE drawing this skin's selected tab in a red of almost
    // exactly this hue, which is the one time in seven that a derived value and a
    // sampled one agreed. It is also the churro cup's colour, so the selected tab
    // is the same red as the Home glyph and the row reads as one object.
    //
    // 4.03:1 on the selected tab's pill against the idle pair's 1.97 on the same
    // ground.
    'madrid': '#AE3F3B',
    // Etna: LAVA, the theme's own crimson — the colour running out of the Home
    // glyph's crater, so the whole tab row is lit by the same thing.
    //
    // It needs no adjusting at all, which is rare here: at a relative luminance of
    // 0.203 it sits in the middle of the window where an _g glyph clears both
    // faces of the glass, and it measures 4.54:1 on the strip's night sky and
    // 4.05 on the charcoal tab fill. The theme's Ember Sand #E2BF79 was the other
    // candidate and fails white at 1.71 — the same trap Prague, Vienna and Madrid
    // all sprang, which is that a theme's most beautiful warm colour is usually
    // its lightest.
    'etna': '#D74C57',
    // Fuji: the PAGODA'S OWN VERMILION, pulled toward the theme's dawn rose so it
    // belongs to the palette rather than shouting over it.
    //
    // Neither of its parents works alone. The literal vermilion #DC5B26 measures
    // 2.82:1 on the blush tab fill — under the floor — and the theme's rose
    // #DD747A only 2.29. Between them, at a luminance of 0.187: 3.79 on the pale
    // body, 3.32 on the tab fill, 4.55 on white and 4.61 on black.
    'fuji': '#B85C61',
    'roraima': '#ED8B8B',          // Pink Quartz; 2.42 white / 8.67 black
    'autumn': '#C7683C',           // the theme's rust; ~4.0 white / ~5.2 black
    'sequoia': '#8CC63F',          // new-growth green, brighter than the token
    'aurora-borealis': '#549EFF',
    'everest': '#ED7E5A',          // sunrise orange, owner's pick over a strict 3:1
    'sakura': '#E898B4',           // the profile-figure cherry pink
    'tokyo': '#CBC8FF',            // light periwinkle, hue nudged toward blue
  };

  let raw = byKey[ p.key ];
  if ( !raw ) {
    // RAW site colour circles, not the boosted electric variants.
    const cp = ( p.compactPalette?.length ? p.compactPalette : p.palette ) || [];
    const t: any = p.designTokens || {};
    const green = cp[ 2 ] || t.secondaryColor || '#1ECFB6';
    const blue = cp[ 1 ] || t.primaryColor || '#549EFF';
    const pink = cp[ 3 ] || t.tertiaryColor || '#BB719B';
    const plan: { [ s: string ]: string } = {
      home: green, voom: green, shopping: green,
      chats: blue, wallet: blue, mini: blue,
      calls: pink, news: pink, today: pink,
    };
    raw = ( slot && plan[ slot ] ) || green;
  }

  // Tokyo ONLY: pull the active glyph into the window that reads on both the
  // dark glass and the lighter pill iOS draws over it. Deliberately not applied
  // to other themes — their exported assets must not move.
  return p.key === 'tokyo' ? glassSafeGlyph( raw ) : raw;
}

/** The IDLE tab glyph colour. */
export function lineTabOff( p: Product ): string {
  const byKey: { [ key: string ]: string } = {
    'miami': MIAMI_FADED_CIRCLE_1,
    // Roraima: a FADED, DARKER Pink Quartz — the ON colour's own hue (0deg) at
    // half its saturation and a step down in lightness (owner 2026-08-14: "o
    // sarilari soluk pembe mi yapsak, ON renginin daha solugu koyusu").
    //
    // It replaces Dry Grass #D9B95F, which was picked to balance the BROWN
    // iOS-26 capsule the design screenshots showed (~1.9:1 there and ~1.9:1 on
    // white). The phone draws LIGHT glass on this theme, so that balance was
    // paid for nothing: at 1.90:1 on white the idle glyphs were simply not there
    // (owner: "light glass kullanmis ve bizim sarilar gorunmuyor").
    //
    // ON's OWN COLOUR, faded and DARKENED (owner 2026-08-14: "ON ile aynı renk
    // yap önce, sonra onun soluğu yap" and then "OFF'lar soluk KOYU olacak, soluk
    // açık değil"). Pink Quartz held at hue 0, its saturation roughly halved and
    // its lightness taken well down: hsl(0 26% 47%) against ON's hsl(0 73% 74%).
    // The idle row is literally the active colour with the life taken out of it.
    //
    // The direction of the fade is the part that matters: the capsule it sits on
    // is itself light, so a PALE fade measured 1.45:1 there and was not on the
    // bar at all. Faded DARK reads.
    //
    // A violet was tried here and reverted — the hue turn belongs to the menu
    // strip's SKY, not to the buttons (owner: "gökyüzü mora çalacak... geri al
    // tuş değişikliklerini"). Separating the glyph from its background by chroma
    // is the right idea; doing it by moving the BACKGROUND keeps the tab row in
    // the theme's own pink and gets the same separation.
    //
    // Four earlier answers are worth recording, because each was wrong in its own
    // way and the route between them is the lesson:
    //
    //   #DDBE6D  Dry Grass yellow   1.24:1 on the capsule — not there at all
    //   #B07070  dusty rose         1.74:1 — measured against WHITE, and this
    //                               theme's iOS 26 capsule is not white; it is a
    //                               milky BROWN #C0A799 (owner's photo of the
    //                               live phone, Downloads 17.47.05)
    //   #3A2D4E  deep violet        read as near-black
    //   #4A3A63  lifted violet      read as grey: at 26% saturation a dark violet
    //                               has no chroma left to lose
    //
    // The chase through violets was after hue separation from ON, on Miami's
    // precedent. It was the wrong goal here. On this skin the two states already
    // separate by SURFACE — active sits on the pill, idle on the bare capsule —
    // so a different hue bought nothing and cost the row its coherence.
    //
    // The capsule figure is measured ON A PHOTOGRAPH OF THE LIVE PHONE, with the
    // glyphs re-tinted in place on the owner's own screenshot rather than on a
    // flat mock of it (Downloads/Screenshot 17.47.05): 3.36:1, against the Dry
    // Grass yellow's 1.24 and the pale fade's 1.45.
    // Paris: ROSE SMOKE. Both of this theme's surfaces are LIGHT — a beige
    // terrace on the classic strip, a near-white capsule on iOS 26 — so on both
    // of them "louder" means darker, and the idle glyph has to be the LIGHTER of
    // the pair or it out-shouts the active one. Rose Smoke sits a full step above
    // the deepened gold: median 1.98:1 on the strip against ON's 3.62, and
    // 2.23:1 on the capsule against ON's 4.21.
    //
    // It is quiet, deliberately. On a light theme an idle tab that measures well
    // is an idle tab that competes, and this pair was inverted once already.
    'paris': '#C79FAE',
    // Rio: CANOPY, a muted Jungle Canopy. Light strip and light capsule both, so
    // the idle glyph is the LIGHTER of the pair on purpose — it has to be present
    // without competing with the deep-green statue beside it. PROVISIONAL, to be
    // measured on the real strip.
    'rio-de-janeiro': '#7FA274',
    // Tallinn: a muted periwinkle. Its strip is a PALE winter sky, so as on Rio
    // and Paris the idle glyph is the lighter of the pair on purpose — 1.97:1 on
    // that sky against the active cobalt's 4.6, present without competing.
    'tallinn': '#8FA3C9',
    // Ocean: WATER SLATE. The device screenshot samples at #507078 and that is
    // what shipped, but it moved WITH the active green when the owner asked for a
    // lighter pair — hold one end and the two collapse into each other.
    //
    // Separated from the active green by HUE — blue-grey against sea-green — the
    // trade Miami and Roraima both settled on. It loses to ON on every surface,
    // which is the ordering that matters: 2.60:1 on the iOS-26 pill against the
    // green's 3.61, 2.46 on the strip's water against 3.41.
    'ocean': '#789399',
    // Salda: PALE STONE, the sandstone taken up rather than the theme's own.
    //
    // Separated from the active sand by WEIGHT alone here, not by hue — the owner
    // asked for both ends to come off the shore, so the trade Miami and Roraima
    // use (warm against cold) is not available and the pair has to be two depths
    // of one material. That works because the gap is wide: 1.63 on the iOS-26
    // pill against the active sand's 2.90, 2.02 on the strip's sky against 3.58.
    //
    // Going paler than this is the limit. At #CCC0AC the idle glyph reads 1.44 on
    // the pill and 1.04 on the tab fill, which is a tab bar with nothing in it.
    'salda': '#C2B49E',
    // Prague: MOONLIT SLATE. Separated from the gold by HUE, which is the trade
    // every dark theme here ends up making, and by weight in the right direction:
    // 3.59:1 on the strip's night sky against the gold's 5.34.
    //
    // The theme's own Mist #92A3B3 was the obvious pick and it is exactly
    // backwards — at 6.58 on that sky it OUT-MEASURES the active gold, which is
    // the inversion this family keeps shipping. An idle row louder than the
    // active one is worse than an idle row nobody can see.
    'prague': '#5B7595',
    // Sydney: SANDSTONE, the theme's own — the rock the whole city is built from,
    // and the warm counterpart to a chrome that is entirely blue.
    //
    // Separated from the active blue by HUE, the trade Miami and Roraima settled.
    // It is the quieter of the pair on every surface, which is the ordering a
    // LIGHT skin needs: 2.40:1 on the tab fill against the blue's 3.58. Held at
    // luminance 0.280 so it still clears the iOS-26 glass both ways (3.18 white,
    // 6.60 black) — the paler #B29551 was tried first and fails white at 2.87.
    // ...and it is MID HARBOUR now, not that sandstone.
    //
    // The gold ordered correctly by weight — 2.40 on the tab fill against the
    // active blue's 3.58 — and still read wrong, which is the case a measurement
    // does not catch. A warm mark ADVANCES against a cool field: on a tab row that
    // is otherwise all harbour blue, the one gold glyph is the one the eye picks,
    // and it picks it as the SELECTED tab. Miami and Roraima separate their pairs
    // by hue too, but there the warm one IS the active one, which is the whole
    // difference.
    //
    // So the pair separates by weight alone, two depths of one water: 2.29 on the
    // tab fill against the active blue's 3.58, 2.94 on the glass against 4.59.
    'sydney': '#6E9AAE',
    // Reynisfjara: SLATE, the theme's own #7D848D — the wet rock the driftwood is
    // lying on. 4.04:1 on the dusk sky against the tan's 4.57, so it loses on
    // weight as well as on temperature, which is the ordering a dark theme wants
    // and the one Prague's first pick got backwards.
    'reynisfjara': '#7D848D',
    // Spring: MEADOW, a muted mid green — the grass the bird is working over.
    // Separated from the rose by HUE, and the quieter of the pair on every
    // surface, which is the ordering a light skin needs: 2.04:1 on the tab fill
    // against the rose's 3.43.
    'spring': '#8AAE86',
    // Vienna: FADED VELVET — the theme's own dusty rose taken down.
    //
    // Its Rose Quartz #D7999F was the obvious pick and is the Prague mistake
    // exactly: at 6.88:1 on the strip's ceiling it OUT-MEASURES the active gold's
    // 4.76, so the idle row would shout over the active one. Down two steps it
    // sits at 3.76 on that ceiling and 3.15 on the wine tab fill — present, plainly quieter, and separated from the gold by hue
    // as well as by weight.
    // Vienna: TAUPE ROSE — the dust on an unplayed instrument. Separated from the
    // sienna by saturation rather than hue, because on a blush this warm there is
    // nowhere cool for an idle glyph to go; it goes quiet instead.
    //
    // 2.84:1, up from 2.07 (owner 2026-08-16: the idle tabs needed to show more).
    // The selected tab is 4.47, so it still outweighs an idle one by better than
    // three to two — which is the point of the pair, and was inverted on this skin
    // before it was sampled.
    'vienna': '#AC8C7A',
    // Madrid: OCHRE — the sunlit plaster of the street the fan is being carried
    // down. Separated from the red by HUE and much the quieter of the pair:
    // 2.10:1 on the tab fill against the red's 4.36. On a skin this warm an idle
    // glyph has nowhere cool to go, so it goes quiet instead.
    // Madrid: OCHRE — the sunlit plaster of the street the cup used to be carried
    // down. Separated from the red by HUE and much the quieter of the pair; on a
    // ground this warm an idle glyph has nowhere cool to go, so it goes quiet.
    // Sampled at #BE904F on the owner's bar, which is where this lands.
    'madrid': '#BE904F',
    // Etna: ASH. Separated from the lava by TEMPERATURE rather than by hue proper,
    // which is all this palette offers — it is basalt and fire and nothing else.
    //
    // The theme's own warm grey #8B8080 was the obvious pick and it is the
    // inversion this family keeps shipping: at 4.40:1 on the charcoal tab fill it
    // OUT-MEASURES the lava's 4.05, so the idle row would be the louder one. Two
    // steps down it sits at 3.01 and the ordering is right.
    // Etna: COOLED BASALT. Separated from the lava by TEMPERATURE rather than by
    // hue proper, which is all this palette offers — it is basalt and fire and
    // nothing else.
    //
    // Deepened from #6E6666, which measured 3.05 on the sampled tab bar against the
    // lava's 4.11: a ratio of 1.35 is not a pair, it is two live tabs. At 2.03 the
    // selected tab outweighs an idle one by two to one.
    'etna': '#524C4C',
    // Fuji: WISTERIA, the theme's own — the colour of the sky an hour before the
    // sun. Separated from the vermilion by hue, and quieter on every surface:
    // 2.78:1 on the tab fill against the vermilion's 3.32.
    // Fuji: WISTERIA, one of the four pagodas' own colours and the quietest of
    // them. Lightened from #AF80A6, which measured 2.93 on the sampled tab bar
    // against the vermilion's 3.99: a ratio of 1.36 is not a pair. At 2.15 the
    // selected tab outweighs an idle one by nearly two to one.
    'fuji': '#C79ABE',
    'roraima': '#965A5A',
    // Istanbul: Bosphorus Teal pulled down two steps, hue held — the theme's
    // Iznik identity kept in the resting state. Palette Bosphorus Teal #1190A1
    // was tried first and failed the owner's "both states must be visible"
    // test: 1.84:1 on the cyan strip is a glyph you have to look for. This
    // lands at 2.55:1 there and 5.26:1 on white, while staying LIGHTER than the
    // ON red so the active tab is still the louder mark on every surface.
    //
    // RESTORED 2026-08-21. It was deleted as collateral in v2026.5.79, the
    // Roraima retune, which rewrote this stretch of the map and took the line
    // above it with it — the commit message never mentions Istanbul. With the
    // entry gone the theme fell through to designTokens.secondaryForeground
    // #0E6876, a value nobody measured against the cyan strip, and the drift
    // only showed when the pack was re-captured a week later.
    'istanbul': '#0E7785',
    'autumn': '#B39A88',
    'sequoia': '#79876F',
    'everest': '#7DA1AF',          // #567B8A read too heavy for the icy theme
    'sakura': '#C0A6B0',           // paler, fainter dusty rose (owner 2026-07-29)
    // Tokyo is a DARK LINE theme, so OFF is a muted LIGHT mauve — a dark OFF
    // would vanish on the plum tab bar.
    'tokyo': '#9C9AC6',
  };
  return byKey[ p.key ]
    || ( p.designTokens as any )?.secondaryForeground || '#85A2B3';
}
